// src/pages/oh-checkin.jsx
// Fullscreen check-in kiosk for one open house. Renders as a fixed overlay
// above the dashboard chrome. Setup (template + PIN) -> guest mode; leaving
// guest mode requires the PIN. Completed sign-ins save immediately; failed
// saves queue in localStorage and retry.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useStateValue } from "../context/StateProvider";
import KioskSetup from "../components/open-houses/checkin/KioskSetup";
import KioskGuestFlow from "../components/open-houses/checkin/KioskGuestFlow";
import { DEFAULT_TEMPLATE } from "../utils/checkinFlow";
import {
  getOpenHouseById, getDealById, saveOpenHouseCheckin, updateCheckinTemplate,
} from "../utils/firebasefunctions";
import { enqueueCheckin, flushCheckinQueue, loadQueue } from "../utils/checkinQueue";

// A pull-to-refresh (or accidental reload) must land back in LOCKED guest
// mode, not the setup screen — otherwise a guest could reach the dashboard.
const KIOSK_SESSION_KEY = "ohKioskSession";

// The stored session for THIS open house, or null.
function loadStoredSession(openHouseId) {
  try {
    const raw = window.sessionStorage.getItem(KIOSK_SESSION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved?.openHouseId === openHouseId && saved.pin ? saved : null;
  } catch {
    return null;
  }
}

export default function OhCheckin() {
  const { openHouseId } = useParams();
  const navigate = useNavigate();
  const [{ user }] = useStateValue();
  const [openHouse, setOpenHouse] = useState(null);
  const [photo, setPhoto] = useState("");
  // false | "waiting" (fetch rejected mid-session, likely offline; retrying)
  // | "missing" (doc confirmed gone or cancelled)
  const [failed, setFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [session, setSession] = useState(null); // { template, pin }
  const [pinPrompt, setPinPrompt] = useState(false);
  const pinRef = useRef("");

  useEffect(() => {
    let alive = true;
    (async () => {
      let oh = null;
      let rejected = false;
      try { oh = await getOpenHouseById(openHouseId); } catch { rejected = true; }
      if (!alive) return;
      // getDoc REJECTS while offline, which does not mean the open house is
      // gone. With a locked kiosk session on this device, a hard-fail here
      // would hand a guest an un-gated exit: hold a locked waiting screen
      // and keep retrying instead.
      if (rejected && loadStoredSession(openHouseId)) { setFailed("waiting"); return; }
      if (!oh || oh.status === "Cancelled") { setFailed("missing"); return; }
      setFailed(false);
      setOpenHouse(oh);
      if (oh.dealId) {
        try {
          const deal = await getDealById(oh.dealId);
          const p = Array.isArray(deal?.propertyphoto) ? deal.propertyphoto[0] : "";
          if (alive && typeof p === "string" && /^https?:\/\//.test(p)) setPhoto(p);
        } catch { /* fallback backdrop */ }
      }
    })();
    return () => { alive = false; };
  }, [openHouseId, retryTick]);

  // While waiting (offline with a locked session), retry the fetch every 5s
  // and immediately on reconnect.
  useEffect(() => {
    if (failed !== "waiting") return;
    const retry = () => setRetryTick((t) => t + 1);
    const iv = setInterval(retry, 5000);
    window.addEventListener("online", retry);
    return () => { clearInterval(iv); window.removeEventListener("online", retry); };
  }, [failed]);

  // Retry any stranded sign-ins from a previous session, and on reconnect.
  // flushCheckinQueue is single-flight, so overlapping triggers are safe.
  useEffect(() => {
    const flush = () => {
      if (loadQueue(window.localStorage).length === 0) return;
      flushCheckinQueue(window.localStorage, saveOpenHouseCheckin);
    };
    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, []);

  // Resume a locked kiosk session after a reload (pull-to-refresh must not
  // drop a guest onto the setup screen).
  useEffect(() => {
    const saved = loadStoredSession(openHouseId);
    if (saved) setSession({ template: saved.template || DEFAULT_TEMPLATE, pin: saved.pin });
  }, [openHouseId]);

  // Trap history-back while guest mode is active (standard kiosk back-trap):
  // push a state on entry, and re-push whenever the guest hits back.
  const inGuestMode = !!session;
  useEffect(() => {
    if (!inGuestMode) return;
    window.history.pushState(null, "", window.location.href);
    const onPop = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [inGuestMode]);

  const start = ({ template, pin }) => {
    setSession({ template, pin });
    // Persist so a reload resumes LOCKED guest mode with the same questions.
    try {
      window.sessionStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify({ openHouseId, pin, template }));
    } catch { /* storage full/blocked: kiosk still works, just won't survive reload */ }
    if (template.id !== "standard") {
      updateCheckinTemplate(template.id, { lastUsedAt: Date.now() });
    }
  };

  const onComplete = async (draft) => {
    const payload = {
      openHouseId,
      dealId: openHouse?.dealId || "",
      templateId: session?.template?.id || "standard",
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      phone: draft.phone.trim(),
      email: draft.email.trim(),
      hasAgent: draft.hasAgent === true,
      agentName: draft.agentName.trim(),
      agentBrokerage: draft.agentBrokerage.trim(),
      agentPhone: draft.agentPhone.trim(),
      intent: draft.intent,
      partySize: Number(draft.partySize) || 1,
      answers: draft.answers,
      // Snapshot of the extra questions as asked. Sort & Send resolves
      // answer ids against THIS, not the live template, which may have
      // been edited or deleted by then.
      askedQuestions: (session?.template?.questions || []).map((q) => ({
        id: q.id,
        prompt: q.prompt,
      })),
    };
    // Offline: skip the save attempt entirely. Firestore's SDK never rejects
    // while offline — the write just waits in memory, and that buffer does
    // not survive a reload. localStorage does.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      enqueueCheckin(window.localStorage, payload);
      return;
    }
    // Race the save against a timeout so a hung write (dead wifi that still
    // reports online) still lands in the queue. Tradeoff: if the timed-out
    // write later commits on reconnect, the queued retry can duplicate that
    // one check-in — a duplicate the agent can Skip in Sort & Send, which is
    // better than a silently lost guest.
    const TIMEOUT = { success: false, timedOut: true };
    const r = await Promise.race([
      saveOpenHouseCheckin(payload).catch(() => ({ success: false })),
      new Promise((res) => setTimeout(() => res(TIMEOUT), 8000)),
    ]);
    if (!r?.success) enqueueCheckin(window.localStorage, payload);
    else flushCheckinQueue(window.localStorage, saveOpenHouseCheckin);
  };

  const tryExit = () => {
    if (!session) { navigate("/dashboard/open-houses"); return; }
    setPinPrompt(true);
    pinRef.current = "";
  };

  // Shared PIN gate: rendered over guest mode AND over the failed screen, so
  // a cancelled or missing open house never becomes an un-gated exit.
  const pinOverlay = pinPrompt && session && (
    <div className="absolute inset-0 z-20 bg-black/70 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl p-6 w-full max-w-xs text-center">
        <h2 className="font-bold text-slate-900 mb-3">Enter PIN to exit</h2>
        <input
          autoFocus
          inputMode="numeric"
          type="password"
          maxLength={4}
          onChange={(e) => {
            pinRef.current = e.target.value.replace(/\D/g, "");
            if (pinRef.current.length === 4) {
              if (pinRef.current === session.pin) {
                try { window.sessionStorage.removeItem(KIOSK_SESSION_KEY); } catch { /* noop */ }
                setPinPrompt(false);
                setSession(null);
                navigate("/dashboard/open-houses");
              } else {
                toast.error("Wrong PIN.");
                e.target.value = "";
                pinRef.current = "";
              }
            }
          }}
          className="w-full border border-slate-300 rounded-lg p-3 text-center tracking-[0.5em] text-xl outline-none focus:border-slate-800"
        />
        <button onClick={() => setPinPrompt(false)} className="mt-3 text-sm text-slate-500">Cancel</button>
      </div>
    </div>
  );

  // Fetch rejected mid-session (likely offline): locked dead-end waiting
  // screen. No navigation controls; retries run in the background.
  if (failed === "waiting") {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 text-white flex flex-col items-center justify-center p-8 text-center">
        <h1 className="font-serif text-3xl mb-2">One moment...</h1>
        <p className="text-white/70">
          We are reconnecting. Please stay on this page, it will refresh on its own.
        </p>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 text-white flex flex-col items-center justify-center p-8 text-center">
        <h1 className="font-serif text-3xl mb-2">This open house isn't available.</h1>
        <p className="text-white/70 mb-6">Please ask your agent for help.</p>
        {/* With a locked session, tryExit demands the PIN; otherwise it
            navigates directly (agent hitting a bad link). */}
        <button onClick={tryExit} className="text-[#d4af6a] underline">
          Back to Open Houses
        </button>
        {pinOverlay}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-[#16213e]">
      {photo && (
        <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to top, rgba(10,15,30,.92) 0%, rgba(10,15,30,.55) 55%, rgba(10,15,30,.35) 100%)" }}
      />

      {/* Discreet exit control (PIN-gated once guest mode is on) */}
      <button
        onClick={tryExit}
        aria-label="Exit check-in"
        className="absolute top-3 right-3 z-10 text-white/30 hover:text-white/70 text-xl px-3 py-1"
      >
        ×
      </button>

      <div className="relative h-full w-full flex items-center justify-center p-8">
        {!openHouse ? (
          <div className="text-white/70">Loading...</div>
        ) : !session ? (
          <KioskSetup
            user={user}
            openHouse={openHouse}
            onStart={start}
            onBack={() => navigate("/dashboard/open-houses")}
          />
        ) : (
          <KioskGuestFlow openHouse={openHouse} template={session.template} onComplete={onComplete} />
        )}
      </div>

      {pinOverlay}
    </div>
  );
}
